import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { catchError } from 'rxjs';
import { CollaborateurService } from '@shared/services/cabinet/collaborateur.service';
import { versPageArrivee } from '../../core/page-arrivee';
import { NotificationService } from '../../ui/notification/notification.service';
import { estErreurAffichee } from '../../ui/erreur-ecran';
import { REFUS_DOSSIER_GERE, SKIP_ERROR_SNACKBAR } from './contexte-http';
import { estDelaiDepasse } from '../../core/interceptors/delai.interceptor';

/** Durée d'affichage d'un message d'erreur du back (messages parfois longs). */
const DURATION = 8000;

export const erreurInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(NotificationService);
  const router = inject(Router);
  const injector = inject(EnvironmentInjector);
  const collaborateurService = inject(CollaborateurService);

  return next(req).pipe(
    catchError(async (error: HttpErrorResponse) => {
      // 432 : pas de message, retour à la page d'arrivée. L'ancien code renvoyait vers
      // /dossiers (page collaborateur) même pour un client.
      // Ouverture d'un dossier : la garde demande elle-même le code d'accès (REFUS_DOSSIER_GERE).
      if (error.status === 432 && !req.context.get(REFUS_DOSSIER_GERE)) {
        if (LOGAIA_COLLAB_BUILD && collaborateurService.isCollab) {
          router.navigate(['dossiers']);
        } else {
          router.navigateByUrl(runInInjectionContext(injector, () => versPageArrivee()));
        }
        throw error;
      }

      // 401 d'un appel authentifié : l'intercepteur d'auth affiche déjà « Vous avez été
      // déconnecté. ». Sans jeton (ex. connexion), message normal.
      const sansDoublon401 = error.status === 401 && req.headers.has('Authorization');
      if (error.status === 420 || sansDoublon401 || req.context.get(SKIP_ERROR_SNACKBAR)) {
        throw error;
      }

      // Message différé d'un instant : rien si l'écran signale lui-même l'échec (état d'erreur, ui/erreur-ecran.ts)
      // ou s'il a déjà affiché son propre message entre-temps.
      const avant = toast.affiches;
      const afficher = (msg: string) =>
        setTimeout(() => {
          if (!estErreurAffichee(error) && toast.affiches === avant) {
            toast.erreur(msg, DURATION);
          }
        });
      let msg = error.error?.Message ?? texteRefus(error) ?? messageValidation(error.error);
      if (
        error.error instanceof Blob &&
        error.error.type === 'application/json'
      ) {
        const text = await error.error.text();
        try {
          const corps = JSON.parse(text);
          msg = corps['Message'] ?? messageValidation(corps);
        } catch {
          msg = undefined;
        }
      }
      msg = msg && msg.length > 200 ? msg.slice(0, 200) + '…' : msg;
      // Message du back : toujours en dev ; en prod pour les 400 seulement (une 500 peut contenir un texte technique :
      // message neutre ci-dessous, décision de Claire du 23/10).
      if (msg && (!environment.prod || error.status === 400)) {
        afficher(msg);
        throw error;
      }
      // Fichier demandé (téléchargement, PDF à ouvrir) : aucun écran ne signale l'échec, message dédié.
      const fichier = req.method === 'GET' && req.responseType === 'blob';
      // Message sans détail technique, en dev comme en prod (le chemin de l'appel ne va qu'en console, en dev).
      const message = estDelaiDepasse(error)
        ? 'Le serveur ne répond pas. Réessayez plus tard.'
        : !error.status
          ? 'Serveur injoignable. Vérifiez votre connexion.'
          : fichier
            ? "Le fichier n'a pas pu être téléchargé. Réessayez plus tard."
            : "L'opération n'a pas pu aboutir. Réessayez plus tard.";
      if (!environment.prod) {
        // Dev : jamais d'échec silencieux, lectures comprises ; l'appel en échec est indiqué en console.
        console.warn(`Appel en échec (${error.status || 'serveur injoignable'}) : ${req.method} ${req.url.replace(/^https?:\/\/[^/]+\//, '')}`);
        afficher(message);
      } else if (req.method !== 'GET' || fichier) {
        // Prod, action de l'utilisateur ou fichier demandé, sans message exploitable : ne jamais échouer en silence.
        // (Les autres lectures en échec sont signalées par l'écran : « Chargement impossible ».)
        afficher(message);
      }
      throw error;
    }),
  );
};

/**
 * Refus du back envoyé en texte simple (BadRequest("…") : environ 150 routes) : la phrase est affichée telle quelle,
 * comme un refus au format { Message }. Seulement pour une 400 (une 500 peut contenir un texte technique).
 */
export function texteRefus(error: HttpErrorResponse): string | undefined {
  return error.status === 400 && typeof error.error === 'string' && error.error.trim() ? error.error.trim() : undefined;
}

/** Message affiché pour une erreur de validation .NET (détail technique en anglais, non affiché). */
const MESSAGE_VALIDATION = 'Certaines informations envoyées sont invalides.';

/**
 * Erreurs de validation ASP.NET (format « problem details ») : { title, errors: { Champ: ['message', …] } }.
 * Les messages sont techniques et en anglais (« The JSON value could not be converted… ») : on affiche
 * un message français générique ; en dev, le détail part dans la console. undefined si ce n'est pas ce format.
 */
function messageValidation(corps: unknown): string | undefined {
  if (!corps || typeof corps !== 'object') {
    return undefined;
  }
  const { errors, title } = corps as { errors?: unknown; title?: unknown };
  const estValidation = (!!errors && typeof errors === 'object') || (typeof title === 'string' && !!title);
  if (!estValidation) {
    return undefined;
  }
  if (!environment.prod) {
    console.warn('Erreur de validation du back :', title, errors);
  }
  return MESSAGE_VALIDATION;
}

/**
 * Message d'un appel en échec affiché par l'écran (appel passé sans message de l'intercepteur, ex. skipError) :
 * texte renvoyé tel quel par le back s'il y en a un, sinon le message par défaut de l'écran.
 */
export function messageErreurHttp(err: HttpErrorResponse, defaut: string): string {
  return typeof err.error === 'string' && err.error ? err.error : defaut;
}
